import { useRef, useState } from 'react'
import { ArrowDown, ArrowUp, Settings2, X } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useClickOutside } from '@/lib/useClickOutside'

export type RowHeight = 'default' | 'compact'

/** A column offered by the panel — its id, display label and whether it's pinned (locked). */
export interface PanelColumn<Id extends string> {
  id: Id
  label: string
  locked?: boolean
}

function SectionTitle({ children }: { children: string }) {
  return (
    <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1.5">
      {children}
    </div>
  )
}

/**
 * Generic gear button + popover for a server-driven table view: page size, visible columns
 * (add / remove / reorder), and — when the caller supplies the handlers — row height and zebra
 * striping. Shared by the deals and contacts tables so both get the same "Table settings" control.
 * Presentational and generic over the column-id type; each table maps its own settings shape onto
 * these props.
 */
export default function TableSettingsPanel<Id extends string>({
  allColumns,
  columns,
  onColumnsChange,
  pageSizes,
  pageSize,
  onPageSizeChange,
  rowHeight,
  onRowHeightChange,
  zebra,
  onZebraChange,
  onReset,
}: {
  allColumns: PanelColumn<Id>[]
  /** Visible column ids in the user's chosen order. */
  columns: Id[]
  onColumnsChange: (columns: Id[]) => void
  pageSizes: readonly number[]
  pageSize: number
  onPageSizeChange: (size: number) => void
  /** Row height + zebra sections render only when their handler is provided. */
  rowHeight?: RowHeight
  onRowHeightChange?: (height: RowHeight) => void
  zebra?: boolean
  onZebraChange?: (zebra: boolean) => void
  /** When provided, a "Reset to default" restores the table's default columns. */
  onReset?: () => void
}) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  useClickOutside(ref, () => setOpen(false), open)

  const byId = new Map(allColumns.map((c) => [c.id, c]))
  const lockedIds = new Set(allColumns.filter((c) => c.locked).map((c) => c.id))
  // Visible columns in the user's order (skipping any whose definition was removed).
  const visible = columns.map((id) => byId.get(id)).filter((c): c is PanelColumn<Id> => !!c)
  const hidden = allColumns.filter((c) => !columns.includes(c.id))
  // Field search: while a query is active, rows show without reorder arrows (indices would shift).
  const ql = query.trim().toLowerCase()
  const shownVisible = visible
    .map((col, i) => ({ col, i }))
    .filter(({ col }) => !ql || col.label.toLowerCase().includes(ql))
  const shownHidden = hidden.filter((c) => !ql || c.label.toLowerCase().includes(ql))

  /** Show a hidden column (append) or hide a visible one (locked columns can't be hidden). */
  function toggleColumn(id: Id) {
    if (columns.includes(id)) {
      if (lockedIds.has(id)) return
      onColumnsChange(columns.filter((c) => c !== id))
    } else {
      onColumnsChange([...columns, id])
    }
  }

  const canMoveUp = (i: number) =>
    i > 0 && !lockedIds.has(visible[i].id) && !lockedIds.has(visible[i - 1].id)
  const canMoveDown = (i: number) =>
    i < visible.length - 1 && !lockedIds.has(visible[i].id) && !lockedIds.has(visible[i + 1].id)

  /** Move a visible column one place earlier/later; locked columns (and their slots) stay put. */
  function moveColumn(i: number, dir: -1 | 1) {
    const j = i + dir
    if (dir === -1 ? !canMoveUp(i) : !canMoveDown(i)) return
    const next = columns.slice()
    const ai = next.indexOf(visible[i].id)
    const aj = next.indexOf(visible[j].id)
    ;[next[ai], next[aj]] = [next[aj], next[ai]]
    onColumnsChange(next)
  }

  return (
    <div
      ref={ref}
      className="relative"
      onKeyDown={(e) => {
        if (e.key === 'Escape') setOpen(false)
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label="Table settings"
        title="Table settings"
        className={cn(
          'p-2 rounded-lg border transition-colors',
          open
            ? 'border-brand-primary/40 bg-brand-primary/10 text-brand-dark'
            : 'border-gray-200 bg-white text-gray-500 hover:text-brand-dark',
        )}
      >
        <Settings2 size={15} />
      </button>

      {open && (
        <div className="absolute right-0 z-30 mt-1 w-72 bg-white border border-gray-200 rounded-xl shadow-lg p-4 space-y-4">
          <section>
            <SectionTitle>Pagination</SectionTitle>
            <div className="flex items-center gap-4">
              {pageSizes.map((size) => (
                <label
                  key={size}
                  className="flex items-center gap-1.5 text-sm text-brand-dark cursor-pointer"
                >
                  <input
                    type="radio"
                    name="pp-table-page-size"
                    checked={pageSize === size}
                    onChange={() => onPageSizeChange(size)}
                    className="accent-brand-primary"
                  />
                  {size}
                </label>
              ))}
            </div>
          </section>

          {onRowHeightChange && (
            <section>
              <SectionTitle>Row height</SectionTitle>
              <div className="flex items-center gap-4">
                {(
                  [
                    ['compact', 'Compact'],
                    ['default', 'Default'],
                  ] as [RowHeight, string][]
                ).map(([value, label]) => (
                  <label
                    key={value}
                    className="flex items-center gap-1.5 text-sm text-brand-dark cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="pp-table-row-height"
                      checked={rowHeight === value}
                      onChange={() => onRowHeightChange(value)}
                      className="accent-brand-primary"
                    />
                    {label}
                  </label>
                ))}
              </div>
            </section>
          )}

          {onZebraChange && (
            <section>
              <label className="flex items-center gap-2 text-sm text-brand-dark cursor-pointer">
                <input
                  type="checkbox"
                  checked={zebra ?? false}
                  onChange={() => onZebraChange(!zebra)}
                  className="accent-brand-primary"
                />
                Zebra striping
              </label>
            </section>
          )}

          <section>
            <div className="flex items-center justify-between mb-1.5">
              <SectionTitle>Columns</SectionTitle>
              {onReset && (
                <button
                  type="button"
                  onClick={onReset}
                  className="text-[11px] text-brand-primary hover:text-brand-dark"
                >
                  Reset to default
                </button>
              )}
            </div>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search fields…"
              className="w-full px-2 py-1.5 mb-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
            />
            <div className="max-h-64 overflow-y-auto space-y-0.5">
              {shownVisible.map(({ col, i }) => (
                <div key={col.id} className="flex items-center gap-1.5 text-sm text-brand-dark">
                  {ql ? (
                    <span className="w-[26px] shrink-0" />
                  ) : (
                    <>
                      <button
                        type="button"
                        disabled={!canMoveUp(i)}
                        onClick={() => moveColumn(i, -1)}
                        aria-label={`Move ${col.label} up`}
                        className="text-gray-300 hover:text-brand-primary disabled:opacity-30"
                      >
                        <ArrowUp size={12} />
                      </button>
                      <button
                        type="button"
                        disabled={!canMoveDown(i)}
                        onClick={() => moveColumn(i, 1)}
                        aria-label={`Move ${col.label} down`}
                        className="text-gray-300 hover:text-brand-primary disabled:opacity-30"
                      >
                        <ArrowDown size={12} />
                      </button>
                    </>
                  )}
                  <span className={cn('flex-1 truncate', col.locked && 'text-gray-400')}>{col.label}</span>
                  {!col.locked && (
                    <button
                      type="button"
                      onClick={() => toggleColumn(col.id)}
                      aria-label={`Hide ${col.label}`}
                      className="text-gray-300 hover:text-danger"
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
              ))}
              {shownHidden.length > 0 && (
                <>
                  <div className="text-[11px] text-gray-400 mt-2 mb-1">Add column</div>
                  {shownHidden.map((col) => (
                    <label
                      key={col.id}
                      className="flex items-center gap-2 text-sm text-brand-dark cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={false}
                        onChange={() => toggleColumn(col.id)}
                        className="accent-brand-primary"
                      />
                      {col.label}
                    </label>
                  ))}
                </>
              )}
              {shownVisible.length === 0 && shownHidden.length === 0 && (
                <p className="text-xs text-gray-400">No fields match.</p>
              )}
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
